/**
 * tutorApplicationAdapter — la candidature du tuteur, du compte servi (`UserResource` d'un
 * `tutor-candidate`) aux formes d'affichage (`UITutorApplication`), et les règles de l'ancien écran
 * qui disent où mène une étape.
 *
 * Port littéral de `tutor/application/TutorApplicationScreen.js` (l'onglet `dashboard` du candidat) :
 *  - le champion d'une étape (`currentStepChampion`) : celui de la motivation, de la pédagogie, de la
 *    technique, et celui de l'accueil pour le profil ;
 *  - une étape « vidéo » (`isVideoStep`) : sa description dit « video » ou « vidéo », ou c'est une
 *    étape technique ;
 *  - l'étape du moment : `application_current_step.step` (1 à 7), sa part faite `completion` ;
 *  - la candidature est finie quand l'étape du moment est la dernière et faite à 100 %
 *    (`applicationFinished`) ;
 *  - le geste de l'étape : le profil (`application-personal-infos`), sinon l'intro du champion
 *    (`intro`, la phrase de l'étape : `intro_suffix` servi, sinon `_video_interview_intro` ou
 *    `_<catégorie>_interview_intro`), qui ouvre l'entretien (`application-interview`) avec sa
 *    catégorie (`<catégorie>_video` ou `<catégorie>_`) et son rang ;
 *  - au navigateur d'un téléphone ou sur Safari, une étape vidéo demande l'app (la boîte).
 *
 * La règle de passage d'une étape à l'autre vit côté backend (`TutorCandidateTrait::getApplicationStep`).
 */
import { championSlugsToNames } from "../../../../../data/constants/championsConstants"
import { type ChampionName } from "../../../../../design-system/atoms/Avatar/ChampionAvatar"
import {
    type UITutorApplication,
    type UITutorApplicationAppRequired,
    type UITutorApplicationStatus,
    type UITutorApplicationStep,
} from "../../../ui/types/tutorCandidature"

/** Une étape telle que `getApplicationSteps()` la sert. */
export type ApiTutorApplicationStep = {
    title?: string | null
    description?: string | null
    test_category?: string | null
    subject_type?: string | null
    intro_suffix?: string | null
    image?: string | null
}

export type ApiTutorCandidate = {
    id?: number | string | null
    firstname?: string | null
    display_name?: string | null
    avatar?: string | null
    is_verified?: boolean | number | null
    candidature_status?: string | null
    application_steps?: ApiTutorApplicationStep[] | null
    application_current_step?: { step?: number | null; completion?: number | null } | null
    profile?: { relevant_subjects?: string[] | null } | null
}

/** `currentStepChampion` de l'ancien écran. */
export function applicationStepChampion(testCategory: string | null | undefined): ChampionName {
    if (testCategory === "motivation") return "tina"
    if (testCategory === "educational") return "zoe"
    if (testCategory === "technical") return "sam"
    return "abou"
}

/** Le champion nommé par la route `intro` (celui de l'accueil par défaut). */
export function championNamed(name: string | null | undefined): ChampionName {
    return name === "tina" || name === "zoe" || name === "sam" ? name : "abou"
}

/** `isVideoStep` de l'ancien écran, pour l'étape elle-même (seule l'étape du moment se lance). */
export function isVideoApplicationStep(step: ApiTutorApplicationStep | null | undefined): boolean {
    const description = step?.description ?? ""
    return description.includes("video") || description.includes("vidéo") || step?.test_category === "technical"
}

/** Le statut servi (`candidatureStatus()`) ; le `null` d'une évaluation favorable pas encore validée. */
export function applicationStatus(served: string | null | undefined): UITutorApplicationStatus {
    if (served === "draft" || served === "pending" || served === "rejected") return served
    return "evaluated"
}

/** L'étape du moment et sa part faite (`application_current_step`). */
export function currentApplicationStep(user: ApiTutorCandidate | null | undefined): {
    step: number
    completion: number
} {
    return {
        step: Number(user?.application_current_step?.step ?? 0),
        completion: Number(user?.application_current_step?.completion ?? 0),
    }
}

/** `applicationFinished` de l'ancien écran : la dernière étape, faite à 100 %. */
export function isApplicationFinished(user: ApiTutorCandidate | null | undefined): boolean {
    const steps = user?.application_steps ?? []
    const { step, completion } = currentApplicationStep(user)
    return steps.length > 0 && step === steps.length && completion >= 100
}

export function toUITutorApplication(user: ApiTutorCandidate | null | undefined): UITutorApplication | null {
    const steps = user?.application_steps
    if (user == null || !Array.isArray(steps) || steps.length === 0) return null
    const { step: current, completion } = currentApplicationStep(user)
    const finished = isApplicationFinished(user)

    return {
        firstName: user.firstname ?? user.display_name ?? "",
        avatarUri: user.avatar ?? null,
        emailUnverified: !user.is_verified,
        // Finie, la candidature est « envoyée » même si le serveur dit encore `draft` (l'ancien écran
        // montrait la fin dès la dernière étape faite).
        status:
            finished && user.candidature_status === "draft" ? "pending" : applicationStatus(user.candidature_status),
        steps: steps.map((step, index): UITutorApplicationStep => ({
            step: index + 1,
            title: step?.title ?? "",
            description: step?.description ?? "",
            champion: applicationStepChampion(step?.test_category),
            // Finie, aucune étape n'est plus « du moment » : la page montre où en est la candidature.
            status: finished || index + 1 < current ? "done" : index + 1 === current ? "current" : "locked",
            completion: index + 1 === current ? Math.max(0, Math.min(100, completion)) : undefined,
            video: isVideoApplicationStep(step),
        })),
    }
}

/** Où mène le geste de l'étape du moment. */
export type ApplicationStepDestination =
    | { kind: "profile" }
    | {
          kind: "interview"
          /** `putNextScreenParams` de l'ancien écran, que l'intro relaie à l'entretien. */
          nextScreenParams: { testCategory: string; stepNum: number }
          intro: {
              name: ChampionName
              introSuffix: string
              nextScreen: "application-interview"
          }
      }

export function applicationStepDestination(
    user: ApiTutorCandidate | null | undefined
): ApplicationStepDestination | null {
    const { step: stepNum } = currentApplicationStep(user)
    const step = user?.application_steps?.[stepNum - 1]
    if (step == null) return null
    if (stepNum === 1) return { kind: "profile" }
    const category = step.test_category ?? ""
    const video = isVideoApplicationStep(step)
    return {
        kind: "interview",
        nextScreenParams: { testCategory: video ? `${category}_video` : `${category}_`, stepNum },
        intro: {
            name: applicationStepChampion(category),
            introSuffix: step.intro_suffix || `_${video ? "video" : category}_interview_intro`,
            nextScreen: "application-interview",
        },
    }
}

/**
 * La boîte « Télécharge l'application mobile » : au web, sur le navigateur d'un téléphone
 * (`isWebMobile`) ou sur Safari (`isSafari`), une étape vidéo ne s'enregistre pas.
 */
export function appRequiredFor(args: { isWeb: boolean; userAgent: string }): UITutorApplicationAppRequired | null {
    if (!args.isWeb) return null
    if (/android|iphone|kindle|ipad/i.test(args.userAgent)) return "mobile-web"
    const ua = args.userAgent.toLowerCase()
    if (ua.includes("safari") && !ua.includes("chrome")) return "safari"
    return null
}

/** La phrase du champion à la fin du profil (`ApplicationPersonalInfos`, après l'envoi). */
export const PROFILE_DONE_TEXT = `Bravo tu as fini cette première étape ! Tu vas pouvoir passer à l'entretien de ${championSlugsToNames.tina}.`

const LAST_STEP_DONE_TEXT =
    "C'était le dernier passage de ton dépôt de candidature. Nous reviendrons vers toi dans les plus brefs délais !"

/** `getCongratsText` de l'ancien `InterviewScreen` : la phrase de fin d'une étape d'entretien (2 à 7). */
export function interviewStepDoneText(stepNum: number, relevantSubjects: string[] | null | undefined): string {
    const subjects = relevantSubjects ?? []
    const both = subjects.includes("literary") && subjects.includes("scientific")
    switch (stepNum) {
        case 2:
            return "Bravo tu as fini cette étape ! Tu va maintenant pouvoir passer à montrer ta motivation en vidéo !"
        case 3:
            return `Super tu as fini cette étape ! Tu vas pouvoir passer à l'entretien de ${championSlugsToNames.zoe}.`
        case 4:
            return "Merci pour tes réponses ! Tu vas maintenant pouvoir passer à l'entretien en vidéo !"
        case 5:
            return `Génial tu as fini cette étape ! Tu vas pouvoir passer à l'entretien de ${championSlugsToNames.sam}.`
        case 6:
            // Un double profil : l'étape 6 est la technique littéraire, la 7 la scientifique.
            return both
                ? "Génial tu as fini cette question ! Tu vas pouvoir passer à la prochaine question !"
                : LAST_STEP_DONE_TEXT
        case 7:
            return LAST_STEP_DONE_TEXT
        default:
            return ""
    }
}
